import {
  getDraftOptions,
  listDraftGenerationsOptions,
} from "@/api/generated/@tanstack/react-query.gen";
import { getActiveStream } from "@/api/generated/sdk.gen";
import type { DraftStatus } from "@/api/generated/types.gen";
import { CopyableId } from "@/components/composite/copyable-id";
import { Page, ResourceError } from "@/components/misc";
import { ProtectedPage } from "@/components/misc/protected";
import { Badge, Breadcrumb, Skeleton } from "@/components/ui";
import { useSubPageProvider } from "@/lib/hooks/use-sub-page";
import { SSEEventType, type SseProgressMessage } from "@/lib/sse/protocol";
import { useSSEStream } from "@/lib/sse/use-sse-stream";
import { ExperimentStepNav } from "@/routes/draft-experiments/-lib/experiment-step-nav";
import { VariantStatCards } from "@/routes/draft-experiments/-lib/variant-stat-cards";
import {
  type ComparisonState,
  DraftStreamProvider,
  type VariantStreams,
} from "@/routes/draft-experiments/-root-lib/stream-context";
import { VariantStreamMount } from "@/routes/draft-experiments/-root-lib/variant-stream-mount";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Outlet, useParams } from "@tanstack/react-router";
import React from "react";

const STATUS_BADGE: Record<
  DraftStatus,
  { label: string; variant: React.ComponentProps<typeof Badge>["variant"]; }
> = {
  draft: { label: "Draft", variant: "secondary" },
  review: { label: "In review", variant: "amber-soft" },
  ready: { label: "Ready", variant: "blue-soft" },
  promoted: { label: "Promoted", variant: "emerald-soft" },
};

const EMPTY_VARIANT_STREAMS: VariantStreams = { variantEval: null, regen: null };

type VariantPending = {
  variantEval: boolean;
  regen: boolean;
};

function DraftExperimentLayout() {
  const { id } = useParams({ from: "/draft-experiments/$id" });
  const { isSubPage, setIsSubPage, Provider: SubPageProvider } = useSubPageProvider();

  const { data: experiment, isLoading, isError } = useQuery({
    ...getDraftOptions({ path: { experiment_id: id } }),
  });

  const generationsQuery = useQuery({
    ...listDraftGenerationsOptions({ path: { experiment_id: id } }),
    enabled: !!experiment,
  });

  const [comparison, setComparison] = React.useState<ComparisonState>(null);

  const generationStream = useSSEStream({
    deltaKeys: ["variant_id", "slot_key"],
    invalidateTags: ["draft-experiments"],
    onEvent: (evt) => {
      if (evt.event === SSEEventType.COMPLETE) {
        const result = evt.data.result as { batch_id: string; generation_ids: string[]; };
        setComparison({ batchId: result.batch_id, generationIds: result.generation_ids });
      }
    },
  });

  const [latestEvalProgress, setLatestEvalProgress] = React.useState<SseProgressMessage | null>(
    null,
  );
  const evalStream = useSSEStream({
    invalidateTags: ["draft-experiments"],
    onEvent: (evt) => {
      if (evt.event === SSEEventType.PROGRESS) {
        setLatestEvalProgress(evt.data);
      } else if (evt.event === SSEEventType.COMPLETE || evt.event === SSEEventType.ERROR) {
        setLatestEvalProgress(null);
      }
    },
  });

  const prevEvalStatusRef = React.useRef(evalStream.state.status);
  React.useEffect(() => {
    const prev = prevEvalStatusRef.current;
    const curr = evalStream.state.status;
    prevEvalStatusRef.current = curr;
    if (curr === "idle") {
      setLatestEvalProgress(null);
    } else if (curr === "streaming" && prev !== "streaming") {
      setLatestEvalProgress(null);
    }
  }, [evalStream.state.status]);

  const [trackedVariants, setTrackedVariants] = React.useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const [pendingByVariant, setPendingByVariant] = React.useState<
    ReadonlyMap<string, VariantPending>
  >(() => new Map());
  const [variantStreams, setVariantStreams] = React.useState<ReadonlyMap<string, VariantStreams>>(
    () => new Map(),
  );

  const registerStreams = React.useCallback((variantId: string, streams: VariantStreams) => {
    setVariantStreams((prev) => {
      const next = new Map(prev);
      next.set(variantId, streams);
      return next;
    });
  }, []);

  const unregisterStreams = React.useCallback((variantId: string) => {
    setVariantStreams((prev) => {
      if (!prev.has(variantId)) return prev;
      const next = new Map(prev);
      next.delete(variantId);
      return next;
    });
  }, []);

  const trackVariant = React.useCallback((variantId: string) => {
    setTrackedVariants((prev) => {
      if (prev.has(variantId)) return prev;
      const next = new Set(prev);
      next.add(variantId);
      return next;
    });
  }, []);

  const setPending = React.useCallback(
    (variantId: string, kind: "variantEval" | "regen", value: boolean) => {
      setPendingByVariant((prev) => {
        const current = prev.get(variantId) ?? { variantEval: false, regen: false };
        if (current[kind] === value) return prev;
        const next = new Map(prev);
        next.set(variantId, { ...current, [kind]: value });
        return next;
      });
    },
    [],
  );

  const onStartConsumed = React.useCallback(
    (variantId: string, kind: "variantEval" | "regen") => setPending(variantId, kind, false),
    [setPending],
  );

  const startVariantEval = React.useCallback(
    async (variantId: string) => {
      trackVariant(variantId);
      setPending(variantId, "variantEval", true);
    },
    [trackVariant, setPending],
  );

  const startRegen = React.useCallback(
    async (variantId: string) => {
      trackVariant(variantId);
      setPending(variantId, "regen", true);
    },
    [trackVariant, setPending],
  );

  const getVariantStreams = React.useCallback(
    (variantId: string): VariantStreams => variantStreams.get(variantId) ?? EMPTY_VARIANT_STREAMS,
    [variantStreams],
  );

  React.useEffect(() => {
    setTrackedVariants(new Set());
    setPendingByVariant(new Map());
    setVariantStreams(new Map());
  }, [id]);

  const discoveryCallIdRef = React.useRef(0);
  const discoveryImplRef = React.useRef<() => Promise<void>>(() => Promise.resolve());
  discoveryImplRef.current = async () => {
    const callId = ++discoveryCallIdRef.current;
    const { data } = await getActiveStream({ path: { experiment_id: id } });
    if (callId !== discoveryCallIdRef.current) return;
    const items = data?.items ?? [];
    const nextTracked = new Set<string>();
    const initialPending = new Map<string, VariantPending>();
    let needsGeneration = false;
    let needsExperimentEval = false;
    for (const item of items) {
      if (item.type === "generation") {
        needsGeneration = true;
      } else if (item.type === "evaluation") {
        needsExperimentEval = true;
      } else if (item.type === "variant_evaluation" && item.variant_id) {
        nextTracked.add(item.variant_id);
        const cur = initialPending.get(item.variant_id) ?? { variantEval: false, regen: false };
        initialPending.set(item.variant_id, { ...cur, variantEval: true });
      } else if (item.type === "regeneration" && item.variant_id) {
        nextTracked.add(item.variant_id);
        const cur = initialPending.get(item.variant_id) ?? { variantEval: false, regen: false };
        initialPending.set(item.variant_id, { ...cur, regen: true });
      }
    }
    setTrackedVariants((prev) => {
      const merged = new Set(prev);
      for (const v of nextTracked) merged.add(v);
      return merged;
    });
    setPendingByVariant((prev) => {
      const merged = new Map(prev);
      for (const [vid, p] of initialPending) {
        const cur = merged.get(vid) ?? { variantEval: false, regen: false };
        merged.set(vid, {
          variantEval: cur.variantEval || p.variantEval,
          regen: cur.regen || p.regen,
        });
      }
      return merged;
    });
    if (needsGeneration && generationStream.state.status !== "streaming") {
      void generationStream.start(`/api/v2/draft-experiments/${id}/generate/stream`);
    }
    if (needsExperimentEval && evalStream.state.status !== "streaming") {
      void evalStream.start(`/api/v2/draft-experiments/${id}/evaluate/stream`);
    }
  };

  const refreshActiveStreams = React.useCallback(() => discoveryImplRef.current(), []);

  React.useEffect(() => {
    void refreshActiveStreams();
  }, [id, refreshActiveStreams]);

  React.useEffect(() => {
    if (generationStream.state.status === "complete" && !generationsQuery.isFetching) {
      generationStream.reset();
    }
  }, [generationStream.state.status, generationsQuery.isFetching]);

  React.useEffect(() => {
    if (evalStream.state.status === "complete" && !generationsQuery.isFetching) {
      evalStream.reset();
    }
  }, [evalStream.state.status, generationsQuery.isFetching]);

  const streamCtx = React.useMemo(
    () => ({
      generationStream,
      evalStream: { ...evalStream, latestProgress: latestEvalProgress },
      variantStreams,
      getVariantStreams,
      startVariantEval,
      startRegen,
      refreshActiveStreams,
      comparison,
      setComparison,
    }),
    [
      generationStream,
      evalStream,
      latestEvalProgress,
      variantStreams,
      getVariantStreams,
      startVariantEval,
      startRegen,
      refreshActiveStreams,
      comparison,
    ],
  );

  if (isLoading) {
    return (
      <Page size="full" className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-64 w-full" />
      </Page>
    );
  }

  if (isError || !experiment) {
    return (
      <Page size="large" className="min-w-[48rem]">
        <ResourceError
          title="Failed to load experiment"
          message="The experiment you're looking for doesn't exist or couldn't be loaded."
        />
      </Page>
    );
  }

  const slots = experiment.content_slots ?? [];
  const variants = experiment.variants ?? [];
  const generations = generationsQuery.data?.items ?? [];
  const hasSlots = slots.length > 0;
  const hasGenerations = variants.length > 0 && generations.length > 0;

  return (
    <Breadcrumb label={experiment.name || "Experiment"} to={`/draft-experiments/${id}`}>
      <Page size="full">
        {!isSubPage && (
          <Page.Header className="space-y-5 pb-4">
            <div className="@container">
              <Page.TitleContainer className="flex-col items-start gap-4 @2xl:flex-row @2xl:items-center">
                <div className="flex flex-col gap-1.5 min-w-0">
                  <Page.Title>{experiment.name}</Page.Title>
                  <div className="flex items-center gap-2">
                    <Badge variant={STATUS_BADGE[experiment.status ?? "draft"].variant}>
                      {STATUS_BADGE[experiment.status ?? "draft"].label}
                    </Badge>
                    <CopyableId value={experiment.draft_experiment_id ?? ""} />
                  </div>
                </div>
                <div className="w-full self-stretch @2xl:ml-auto @2xl:w-auto">
                  <VariantStatCards variants={variants} generations={generations} />
                </div>
              </Page.TitleContainer>
            </div>
            <ExperimentStepNav
              id={id}
              hasSlots={hasSlots}
              hasGenerations={hasGenerations}
              status={experiment.status ?? null}
            />
          </Page.Header>
        )}

        {Array.from(trackedVariants).map((variantId) => {
          const pending = pendingByVariant.get(variantId) ?? { variantEval: false, regen: false };
          return (
            <VariantStreamMount
              key={variantId}
              experimentId={id}
              variantId={variantId}
              pendingStartVariantEval={pending.variantEval}
              pendingStartRegen={pending.regen}
              onStartConsumed={onStartConsumed}
              registerStreams={registerStreams}
              unregisterStreams={unregisterStreams}
            />
          );
        })}

        <DraftStreamProvider value={streamCtx}>
          <SubPageProvider value={setIsSubPage}>
            <Outlet />
          </SubPageProvider>
        </DraftStreamProvider>
      </Page>
    </Breadcrumb>
  );
}

export const Route = createFileRoute("/draft-experiments/$id")({
  component: () => (
    <ProtectedPage requires="org:draft_experiments:read">
      <DraftExperimentLayout />
    </ProtectedPage>
  ),
});
